import './BPMTable.scss';
import cn from 'classnames';
import {
  Dispatch,
  JSX,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { MdOutlineFileDownload } from 'react-icons/md';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { FilterBarTokens, Option } from '@blisspointmedia/bpm-types/dist/FilterBar';
import { download } from '../../utils/download-utils';
import { useStateFunction } from '../../utils/hooks/useData';
import { usePrePaintSize, useScrollbarSizes } from '../../utils/hooks/useDOMHelpers';
import { Appearance, Design, Size } from '../designConstants';
import { FilterBar } from '../FilterBar';
import { IconButton } from '../IconButton';
import { DraggableRow } from './DraggableRow';
import { FilteredEmptyState } from './FilteredEmptyState';
import { Pagination } from './Pagination';
import { basicRenderer, CellRenderer, StickyTable, SuperTopItemType } from './StickyTable';
import { withColumnAlign } from './sticky-table-utils';
import { SortParam } from './sort-header-utils';
import { getDensityClassName, getDensityRowHeight, TableDensity } from './table-density';
import {
  AUTO_HEIGHT_SKELETON_ROW_COUNT,
  buildSkeletonRows,
  getSkeletonRowCount
} from './table-skeleton-utils';
import { useSortHeader } from './useSortHeader';

interface Dimensions {
  height: number;
  width: number;
}

interface FilterOption {
  name: string;
  label: string;
  type?: 'string' | 'boolean';
}

type ScrollTo = (row: number, col: number) => void;

interface FilteredData {
  content: boolean | Date | number | string;
  originalRowIndex: number;
}

export const ColumnAlign = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right'
} as const;
export type ColumnAlignType = (typeof ColumnAlign)[keyof typeof ColumnAlign];

export interface Header {
  // Aligns every row of the column: header, body, totals and loading placeholders. Unset keeps the
  // table's centered default. A custom cell or totals renderer must keep the `classes` it is given.
  align?: ColumnAlignType;
  contentGetter?: (data: any) => boolean | Date | number | string;
  default?: boolean | Date | number | string;
  className?: string;
  field?: string;
  flex?: boolean | number;
  infoTextParagraphs?: string[];
  isInvalid?: (value: any) => boolean;
  isMulti?: boolean;
  label?: string;
  min?: number;
  max?: number;
  minFlexWidth?: number;
  modalFlex?: number;
  modalRow?: string | number | boolean;
  modalWidth?: number;
  name?: string;
  nonFilterable?: boolean;
  nonInteractive?: boolean;
  optionsGetter?: (data: any) => boolean | Date | number | string | string[];
  options?: string;
  renderer?: (value?, index?) => boolean | Date | null | number | string | JSX.Element;
  diffRenderer?: (rows?) => ReactNode;
  sortAscending?: boolean;
  sortPriority?: number;
  step?: number;
  type?: string;
  width?: number;
  uneditable?: boolean;
  restriction?: (value: any) => boolean;
}

export interface TypedHeader<T> extends Header {
  contentGetter?: (data: T) => boolean | Date | number | string;
  isInvalid?: (value: T) => boolean;
  optionsGetter?: (data: T) => boolean | Date | number | string | string[];
  renderer?: (value: T, index: number) => boolean | Date | null | number | string | JSX.Element;
  diffRenderer?: (rows: { oldRow: T; newRow: T }) => ReactNode;
  restriction?: (value: T) => boolean;
}

interface HoverProps {
  onMouseOut?: (args: any) => void;
  onMouseOver?: (args: any) => void;
}

interface InnerTableProps<TableDataType, TopDataType, BottomDataType, SuperTopDataType> {
  alternateColors?: boolean;
  bottomRowHeight?: number;
  bottomData?: BottomDataType[];
  bottomRenderer: CellRenderer<BottomDataType> | CellRenderer<any>;
  cellRenderer: CellRenderer<TableDataType>;
  data: TableDataType[][];
  // Stretches the grid to the AutoSizer-measured height even when there aren't enough rows to
  // need scrolling -- otherwise StickyTable sizes itself to content, leaving a gap between the
  // last row and the bottom of whatever ancestor gave BPMTable its height (e.g. a WidgetContainer
  // with `fillHeight`). Not wanted when `autoHeight` is on, since that mode explicitly sizes to
  // content instead.
  fillContainer?: boolean;
  headerHeight?: number;
  headers: Header[];
  height: number;
  minColumnWidth: number;
  overscan?: number;
  rowHeight?: number | ((index) => number);
  rowHoverClass?: string;
  reserveVerticalScrollbar: boolean;
  scrollbarSizes: Dimensions;
  scrollToRef?: RefObject<ScrollTo>;
  superHeaderHeight?: number;
  superHeaders?: SuperTopItemType<SuperTopDataType>[];
  superHeadersRenderer: CellRenderer<SuperTopDataType>;
  topData: TopDataType[];
  topRenderer: CellRenderer<TopDataType>;
  width: number;
}

const InnerTable = <TData, TPData, BDData, STData>(
  props: InnerTableProps<TData, TPData, BDData, STData>
): JSX.Element => {
  const {
    alternateColors,
    bottomRowHeight,
    bottomData,
    bottomRenderer,
    cellRenderer,
    data,
    fillContainer = false,
    headerHeight,
    headers,
    height,
    minColumnWidth,
    overscan,
    rowHeight,
    rowHoverClass,
    reserveVerticalScrollbar,
    scrollbarSizes,
    scrollToRef,
    superHeaderHeight,
    superHeaders,
    superHeadersRenderer,
    topData,
    topRenderer,
    width
  } = props;

  const columnWidth: (index: number) => number = useMemo(() => {
    let nonFlexWidth = reserveVerticalScrollbar ? scrollbarSizes.width : 0;
    let flexSum = 0;

    const headerSizes: {
      flex?: number;
      minFlexWidth?: number;
      width?: number;
    }[] = [];

    for (const { width: headerWidth, minFlexWidth, flex: flexGeneric } of headers) {
      let flex: number | undefined;
      if (typeof flexGeneric === 'boolean') {
        flex = flexGeneric ? 1 : 0;
      } else {
        flex = flexGeneric;
      }
      const ourWidth = headerWidth ?? minColumnWidth;
      if (flex) {
        flexSum += flex;
      } else {
        nonFlexWidth += headerWidth ?? minColumnWidth;
      }
      headerSizes.push({
        flex,
        width: ourWidth,
        minFlexWidth
      });
    }

    let flexElementTooSmall;
    let flexScraps;
    do {
      flexElementTooSmall = false;
      flexScraps = 0;
      const perFlexUnitWidthRaw = (width - nonFlexWidth) / flexSum;
      const perFlexUnitWidth = Math.floor(perFlexUnitWidthRaw);
      const perFlexUnitScrap = perFlexUnitWidthRaw - perFlexUnitWidth;
      for (let i = 0; i < headerSizes.length; ++i) {
        const { flex, minFlexWidth } = headerSizes[i];
        if (flex) {
          const actualMin = Math.max(minFlexWidth || 0, minColumnWidth || 0);
          const ourWidth = perFlexUnitWidth * flex;
          if (ourWidth < actualMin) {
            flexElementTooSmall = true;
            headerSizes[i] = {
              width: actualMin
            };
            nonFlexWidth += actualMin;
            flexSum -= flex;
          } else {
            flexScraps += perFlexUnitScrap * flex;
            headerSizes[i] = {
              width: ourWidth,
              minFlexWidth,
              flex
            };
          }
        }
      }
    } while (flexElementTooSmall);

    const headerWidths = headerSizes.map(s => s.width) as number[];
    const scrapDistribution = Math.floor(flexScraps / headerWidths.length);
    let scrapBonuses = flexScraps % headerWidths.length;
    for (let i = 0; i < headerSizes.length; ++i) {
      headerWidths[i] += scrapDistribution;
      if (scrapBonuses > 1) {
        headerWidths[i] += 1;
        scrapBonuses--;
      }
    }

    return i => headerWidths[i];
  }, [minColumnWidth, width, headers, reserveVerticalScrollbar, scrollbarSizes.width]);
  return (
    <StickyTable
      alternateColors={alternateColors}
      bottomData={bottomData}
      bottomHeight={bottomRowHeight}
      bottomRenderer={bottomRenderer}
      cellRenderer={cellRenderer}
      columnWidth={columnWidth}
      data={data}
      fillContainer={fillContainer}
      height={height}
      overscan={overscan}
      rowHeight={rowHeight}
      rowHoverClass={rowHoverClass}
      scrollToRef={scrollToRef}
      superTopData={superHeaders}
      superTopHeight={superHeaderHeight}
      superTopRenderer={superHeadersRenderer}
      topData={topData}
      topHeight={headerHeight}
      topRenderer={topRenderer}
      width={width}
    />
  );
};

const superHeadersDefaultRenderer = ({ data, style, classes }) => (
  <div
    style={style}
    className={cn(classes, 'bpm-super-header', { 'bpm-super-header-with-data': !!data })}
  >
    {data}
  </div>
);

const skeletonCellRenderer: CellRenderer<null> = ({ style, classes }) => (
  <div style={style} className={cn(classes, 'sticky-table-text-cell')}>
    <div className='bpm-table-skeleton-bar' aria-hidden />
  </div>
);

interface DefaultTokens {
  advanced?: string[];
  basic?: string[];
}

interface BPMTableProps<TableDataType, SuperTopDataType> {
  draggableRows?: boolean;
  autoHeight?: boolean;
  onMoveRow?: (dragIndex: number, hoverIndex: number) => void;
  additionalControls?: boolean | number | JSX.Element | string;
  additionalFilterOptions?: FilterOption[];
  alternateColors?: boolean;
  bottomRowHeight?: number;
  className?: string;
  cellClassName?: string;
  cellRenderer?: CellRenderer<TableDataType>;
  csvDownload?: string;
  data?: TableDataType[];
  defaultAdvancedFilter?: boolean;
  defaultTokens?: DefaultTokens;
  dynamicTokens?: string[];
  filterBar?: boolean;
  headerHeight?: number;
  headers?: Header[];
  headersRenderer?: CellRenderer<Header>;
  // Keeps the filter bar and header row mounted and fills the body with skeleton rows at the
  // real row height and column widths. Takes precedence over data and noRowsRenderer.
  isLoading?: boolean;
  limit?: number;
  minColumnWidth?: number;
  noRowsRenderer?: (args?) => JSX.Element;
  onCsvChange?: (downloadCsvContents: string) => void;
  onFilteredDataChange?: (value?: any) => void;
  // Fires whenever the filter bar's own tokens change (add/remove/clear a clause, or a
  // dynamicTokens push) -- NOT on the initial mount from defaultTokens/sessionStorage. Lets a
  // caller keep external state (e.g. a URL query param) in sync with the live filter.
  onTokensChange?: (tokens: FilterBarTokens) => void;
  onUpdateSortParamList?: (params: any) => void;
  overrideSortParamList?: SortParam[];
  singleColumnSort?: boolean;
  overscan?: number;
  pagination?: boolean;
  // Sets row height, header height and cell padding/font. Leave unset to keep the legacy 50px rows.
  density?: TableDensity;
  /** @deprecated use `density`; for variable rows use `rowHeightGetter` */
  rowHeight?: number | ((index) => number);
  rowHeightGetter?: (row: TableDataType) => number; // Per-row height resolved by row identity (correct under sort/filter/pagination). Takes precedence over rowHeight when set.
  rowHoverClass?: string;
  scrollToRef?: RefObject<ScrollTo>;
  setDynamicTokens?: (() => void) | Dispatch<SetStateAction<any>>;
  // Disambiguates saved-filter sessionStorage when a page renders more than one filterable
  // BPMTable at the same pathname -- see useFilterBarTokens.ts. Defaults to `className`, which is
  // NOT guaranteed distinct across a page's tables -- callers with more than one filterable
  // BPMTable at one route must set this explicitly (or give each table a distinct className) to
  // avoid two tables silently sharing one saved filter state.
  storageKeySuffix?: string;
  superHeaderHeight?: number;
  superHeaders?: SuperTopItemType<SuperTopDataType>[];
  superHeadersRenderer?: CellRenderer<SuperTopDataType>;
  totals?: Partial<TableDataType>;
  totalsRenderer?: CellRenderer<Element | number | string | undefined>;
  emptyDisplay?: string;
  // `null` means the portal target is not mounted yet (a callback ref held in state), so the
  // filter bar renders nowhere for that frame instead of inline. Leave unset to render inline.
  externalFilterBarContainer?: Element | null;
}

// /*
//    Unless autoHeight is enabled, the wrapping elements of a BPMTable need a defined
//    height or AutoSizer will give the table 0 height.
// */
export const BPMTable = <TData extends object = object, STData extends string | object = object>(
  props: BPMTableProps<TData, STData> & {
    ref?: RefObject<HTMLDivElement | HTMLInputElement>;
  }
): JSX.Element => {
  const {
    draggableRows = false,
    autoHeight = false,
    onMoveRow,
    additionalControls,
    additionalFilterOptions = [],
    alternateColors,
    bottomRowHeight,
    className = '',
    cellClassName = '',
    cellRenderer = basicRenderer,
    csvDownload,
    data = [],
    defaultAdvancedFilter = false,
    defaultTokens,
    dynamicTokens = [],
    filterBar = true,
    headerHeight,
    headers = [],
    headersRenderer,
    isLoading = false,
    limit = 10,
    minColumnWidth = 100,
    noRowsRenderer,
    onCsvChange,
    onFilteredDataChange,
    onTokensChange,
    onUpdateSortParamList = () => {},
    overrideSortParamList,
    singleColumnSort = false,
    overscan,
    pagination = false,
    density,
    rowHeight = getDensityRowHeight(density),
    rowHeightGetter,
    rowHoverClass,
    scrollToRef,
    setDynamicTokens,
    storageKeySuffix,
    superHeaderHeight,
    superHeaders,
    superHeadersRenderer = superHeadersDefaultRenderer,
    totals,
    totalsRenderer = basicRenderer,
    emptyDisplay,
    externalFilterBarContainer
  } = props;
  const originalData = useMemo(() => data.filter(elem => elem != null), [data]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollbarSizes = useScrollbarSizes(containerRef);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [tableWidth, setTableWidth] = useState<number>();
  const [offset, updateOffset] = useState(0);
  const [filter, setFilterRaw] = useStateFunction<(value: any) => boolean>(() => true);
  // Track the current FilterBar tokens so we can distinguish "no rows because filter matched
  // nothing" from "no rows because the underlying data is empty". FilterBar emits `[]` on clear
  // and on mount before the user types, so `filterTokens.length > 0` doubles as "user has
  // entered a filter".
  const [filterTokens, setFilterTokens] = useState<Option[]>([]);

  const setFilter = useCallback(
    (filterFn: (value: any) => boolean, tokens: Option[]) => {
      setFilterTokens(tokens);
      if (onFilteredDataChange) {
        onFilteredDataChange(originalData.filter(filterFn));
      }
      setFilterRaw(filterFn);
    },
    [onFilteredDataChange, originalData, setFilterRaw]
  );

  // Data comes in as an array of objects. The keys on the objects are targets for the filter. We should take an array
  // of headers that include a label and a name. We can map over our data and convert it to a 2d array and filter along
  // the way.
  const filteredData = useMemo(() => {
    const result: FilteredData[][] = [];
    for (let i = 0; i < originalData.length; ++i) {
      const obj = originalData[i];
      if (filter(obj)) {
        const row: FilteredData[] = [];
        for (const { name, contentGetter } of headers) {
          const content = contentGetter ? contentGetter(obj) : obj[name ?? 'undefined'];
          row.push({
            originalRowIndex: i,
            content: content ?? ''
          });
        }
        result.push(row);
      }
    }
    return result;
  }, [originalData, headers, filter]);

  const topData = useMemo(() => headers.map(h => h.label ?? ''), [headers]);
  const bottomData = useMemo(
    () => totals && headers.map(header => (header && header.name ? totals[header.name] : 0)),
    [totals, headers]
  );

  const initialSortParamList = useMemo(() => {
    const sorts: { index: number; ascending: boolean; priority: number }[] = [];
    for (let i = 0; i < headers.length; ++i) {
      if (headers[i].sortPriority != null) {
        sorts.push({
          index: i,
          ascending: headers[i].sortAscending ?? false,
          priority: headers[i].sortPriority || 0
        });
      }
    }
    return [...sorts]
      .sort((a, b) => a.priority - b.priority)
      .map(({ index, ascending }) => ({ index, ascending }));
  }, [headers]);

  const [sortedData, topRenderer] = useSortHeader({
    data: filteredData,
    disableMap: headers.map(h => h.nonInteractive),
    getter: (elem: any) => elem.content,
    initialSortParamList: overrideSortParamList ?? initialSortParamList,
    onUpdateSortParamList,
    renderer: headersRenderer as any,
    updateOffset,
    singleColumnSort
  }) as [any[], CellRenderer<any>];

  const getColumnAlign = useCallback(
    (columnIndex: number) => headers[columnIndex]?.align,
    [headers]
  );
  const alignedTopRenderer = useMemo(
    () => withColumnAlign(topRenderer, getColumnAlign),
    [topRenderer, getColumnAlign]
  );

  const ourCellRenderer: CellRenderer<any> = useCallback(
    ({ data: cellData, style, classes, columnIndex, rowIndex, onMouseOver, onMouseOut }) => {
      const { renderer, className: headerClassName } = headers[columnIndex];
      const allClasses = [...classes, cellClassName, headerClassName].filter(Boolean) as string[];

      const row = originalData[cellData.originalRowIndex];

      const cellValue =
        (cellData?.content == null || cellData.content === '') && !renderer && emptyDisplay != null
          ? emptyDisplay
          : cellData?.content;

      const rowContent = () => {
        if (!renderer) {
          return cellRenderer({
            classes: allClasses,
            columnIndex,
            data: cellValue,
            onMouseOut,
            onMouseOver,
            rowData: row,
            rowIndex,
            style
          });
        }
        const hoverProps: HoverProps = {};
        if (onMouseOut) {
          hoverProps.onMouseOut = onMouseOut;
        }
        if (onMouseOver) {
          hoverProps.onMouseOver = onMouseOver;
        }
        return (
          <div className={allClasses.join(' ')} style={style} {...hoverProps}>
            {renderer(originalData[cellData.originalRowIndex], rowIndex) as ReactNode}
          </div>
        );
      };

      return draggableRows ? (
        <DraggableRow index={rowIndex} moveRow={onMoveRow as any}>
          {rowContent()}
        </DraggableRow>
      ) : (
        rowContent()
      );
    },
    [headers, originalData, cellRenderer, cellClassName, draggableRows, onMoveRow, emptyDisplay]
  );

  // Header.align applies to every row of a column: header, body, totals and loading placeholders.
  const alignedCellRenderer = useMemo(
    () => withColumnAlign(isLoading ? skeletonCellRenderer : ourCellRenderer, getColumnAlign),
    [isLoading, ourCellRenderer, getColumnAlign]
  );
  const alignedTotalsRenderer = useMemo(
    () => withColumnAlign(totalsRenderer as CellRenderer<any>, getColumnAlign),
    [totalsRenderer, getColumnAlign]
  );

  // Only build the CSV when it's actually consumed — the built-in download button (`csvDownload`)
  // or an external `onCsvChange` listener. Otherwise skip the O(rows × cols) pass on every mount.
  const csvEnabled = Boolean(csvDownload) || Boolean(onCsvChange);
  const downloadCsvContents: string = useMemo(() => {
    if (!csvEnabled) return '';
    const headerString = `"${headers.map(h => h.label).join('","')}"`;
    const contentArr: string[] = sortedData.map(
      (elem: FilteredData[]) => `"${elem.map(e => e.content).join('","')}"`
    );
    return [headerString, ...contentArr].join('\n');
  }, [csvEnabled, sortedData, headers]);

  useEffect(() => {
    onCsvChange?.(downloadCsvContents);
  }, [downloadCsvContents, onCsvChange]);

  const downloadCSV = useCallback(() => {
    download(downloadCsvContents, `${csvDownload}.csv`, 'text/csv');
  }, [downloadCsvContents, csvDownload]);

  const filterBarContent =
    filterBar || csvDownload || additionalControls ? (
      <div className='filter-bar-component' data-testid='filter-bar'>
        {filterBar && (
          <FilterBar
            size={Size.LG}
            options={
              [
                ...headers.filter((el: Header) => !el.nonInteractive && !el.nonFilterable),
                ...additionalFilterOptions
              ] as FilterOption[]
            }
            lines={originalData}
            onFilter={setFilter}
            onChange={onTokensChange}
            defaultTokens={defaultTokens}
            defaultAdvanced={defaultAdvancedFilter}
            dynamicTokens={dynamicTokens}
            setDynamicTokens={setDynamicTokens}
            storageKeySuffix={storageKeySuffix ?? (className || undefined)}
            hasAdvanced
          />
        )}
        {csvDownload && (
          <IconButton
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            onClick={downloadCSV}
          >
            <MdOutlineFileDownload />
          </IconButton>
        )}
        {additionalControls}
      </div>
    ) : null;

  // Opt-in per-row height by source-data identity. rowHeightGetter receives the
  // actual row object (resolved via originalRowIndex), so heights stay correct
  // regardless of sort/filter/pagination order. When it is not provided, rowHeight
  // is used exactly as before (a number, or a positional-index function) — no
  // behavior change for existing callers.
  const renderData = pagination ? sortedData.slice(offset, offset + limit) : sortedData;
  const resolvedRowHeight = rowHeightGetter
    ? (index: number): number => {
        const originalRowIndex =
          (renderData[index]?.[0] as { originalRowIndex?: number } | undefined)?.originalRowIndex ??
          index;
        return rowHeightGetter(originalData[originalRowIndex]);
      }
    : rowHeight;
  // When the filter bar has active tokens but no rows match, render a shared "No results for
  // ..." message instead of the caller's noRowsRenderer. Preempting here (rather than exposing
  // filter state to consumers) keeps the copy consistent across every table without requiring
  // per-page opt-in. The truly-empty case (no underlying data at all) still routes to the
  // caller's noRowsRenderer so page-specific empty states (e.g. loading spinners, custom copy)
  // keep working.
  const isFilteredEmpty =
    sortedData.length === 0 && filterTokens.length > 0 && originalData.length > 0;

  const rendersInnerTable =
    isLoading || sortedData.length > 0 || (!isFilteredEmpty && !noRowsRenderer);
  const prePaintSize = usePrePaintSize(tableContainerRef, rendersInnerTable);
  const defaultRowHeight = typeof rowHeight === 'number' ? rowHeight : getDensityRowHeight(density);
  const headerRowsHeight =
    (headers.length ? (headerHeight ?? defaultRowHeight) : 0) +
    (superHeaders?.length ? (superHeaderHeight ?? defaultRowHeight) : 0);
  const visibleBottomData = isLoading ? undefined : bottomData;
  const bodyHeight = isLoading
    ? AUTO_HEIGHT_SKELETON_ROW_COUNT * defaultRowHeight
    : renderData.reduce(
        (height, _row, index) =>
          height +
          (typeof resolvedRowHeight === 'function'
            ? resolvedRowHeight(index)
            : (resolvedRowHeight ?? defaultRowHeight)),
        0
      );
  const naturalContentHeight = rendersInnerTable
    ? headerRowsHeight +
      (visibleBottomData ? (bottomRowHeight ?? defaultRowHeight) : 0) +
      bodyHeight
    : defaultRowHeight;
  const minimumTableWidth = headers.reduce(
    (width, header) =>
      width +
      (header.flex
        ? Math.max(header.minFlexWidth ?? 0, minColumnWidth)
        : (header.width ?? minColumnWidth)),
    0
  );
  const hasHorizontalOverflow =
    rendersInnerTable && tableWidth != null && minimumTableWidth > tableWidth;
  // FilteredEmptyState owns its intrinsic minimum; virtualized tables need an
  // explicit viewport height, including room for a real horizontal scrollbar.
  const contentHeight =
    autoHeight && !isFilteredEmpty
      ? naturalContentHeight + (hasHorizontalOverflow ? scrollbarSizes.height : 0)
      : undefined;

  const innerTable = (
    <AutoSizer
      onResize={({ width }) => setTableWidth(width)}
      renderProp={({ width = prePaintSize?.width ?? 0, height = prePaintSize?.height ?? 0 }) => (
        <InnerTable
          alternateColors={alternateColors}
          bottomRowHeight={bottomRowHeight}
          bottomData={visibleBottomData}
          bottomRenderer={alignedTotalsRenderer}
          cellRenderer={alignedCellRenderer}
          data={
            isLoading
              ? buildSkeletonRows(
                  autoHeight
                    ? AUTO_HEIGHT_SKELETON_ROW_COUNT
                    : getSkeletonRowCount(
                        height -
                          headerRowsHeight -
                          (minimumTableWidth > width ? scrollbarSizes.height : 0),
                        defaultRowHeight
                      ),
                  headers.length
                )
              : renderData
          }
          fillContainer={!autoHeight}
          headerHeight={headerHeight ?? defaultRowHeight}
          headers={headers}
          height={height}
          minColumnWidth={minColumnWidth}
          overscan={overscan}
          rowHeight={isLoading ? defaultRowHeight : resolvedRowHeight}
          rowHoverClass={isLoading ? undefined : rowHoverClass}
          // Fill-container tables never reserve a gutter: StickyTable grows the view by the
          // scrollbar size and the wrapper clips the bars.
          reserveVerticalScrollbar={
            autoHeight &&
            !isLoading &&
            naturalContentHeight > height - (minimumTableWidth > width ? scrollbarSizes.height : 0)
          }
          scrollbarSizes={scrollbarSizes}
          scrollToRef={scrollToRef}
          superHeaderHeight={superHeaderHeight}
          superHeaders={superHeaders}
          superHeadersRenderer={superHeadersRenderer}
          topData={topData}
          topRenderer={alignedTopRenderer}
          width={width}
        />
      )}
    />
  );

  const tableBody = (() => {
    if (isLoading || sortedData.length > 0) return innerTable;
    if (isFilteredEmpty) return <FilteredEmptyState tokens={filterTokens} />;
    if (noRowsRenderer) return noRowsRenderer();
    // No data + no renderer -- preserve the current default (an empty AutoSizer table).
    return innerTable;
  })();

  return (
    <div
      ref={containerRef}
      className={cn('bpm-table', getDensityClassName(density), className, {
        'auto-height': autoHeight
      })}
    >
      {externalFilterBarContainer === undefined
        ? filterBarContent
        : externalFilterBarContainer &&
          filterBarContent &&
          createPortal(filterBarContent, externalFilterBarContainer)}
      <div
        ref={tableContainerRef}
        className='bpm-table-container'
        style={{ height: contentHeight }}
        aria-busy={isLoading}
        data-testid={isLoading ? 'table-skeleton' : undefined}
      >
        {isLoading && (
          <span role='status' className='bpm-table-loading-label'>
            Loading
          </span>
        )}
        {tableBody}
      </div>
      {pagination && updateOffset && (
        <Pagination
          limit={limit}
          offset={offset}
          totalData={data?.length}
          updateOffset={updateOffset}
        />
      )}
    </div>
  );
};
